import {
  useCallback, useEffect, useState,
} from 'react';
import {
  IMarketplace,
  IMarketplaceProperty, Option,
} from 'src/components/profileComponents/Marketplace/types';
import {
  getMarketplaceById,
  getMarketplaceLanguageObjects,
  getMarketplaces,
} from 'src/api/marketplace';
import { useGetAuthData } from 'src/hooks/use-get-auth-data';
import { IMarketplaceLanguageObjects, IMarketplaceLanguageObjectsData } from 'src/typings/marketplace';
import { useDispatch, useSelector } from 'react-redux';
import { Actions } from 'src/store/marketplace/actions';
import { getAllMarketplace } from 'src/store/marketplace/selectors';

export const useMarketplaces = () => {
  const marketplaces = useSelector(getAllMarketplace);
  const [marketplace, setMarketplace] = useState<IMarketplace>();
  const [marketplaceProperties, setMarketplaceProperties] = useState<IMarketplaceProperty[]>([]);
  const [marketplaceLanguageObjects, setMarketplaceLanguageObjects] = useState<IMarketplaceLanguageObjects | undefined>();
  const [marketplaceLanguageObjectsOptions, setMarketplaceLanguageObjectsOptions] = useState<Option<string>[]>([]);
  const [marketplaceLanguageObjectsIsLoading, setMarketplaceLanguageObjectsIsLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [marketplacePropertiesIsLoading, setMarketplacePropertiesIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();

  const fetchMarketplaces = () => {
    setIsLoading(true);
    getMarketplaces(jwt)
      .then((res) => {
        const { data } = res;
        dispatch(Actions.setAllMarketplaces(data['hydra:member']));
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const fetchShortMarketplace = (id: number) => {
    setIsLoading(true);
    getMarketplaceById(jwt, id)
      .then((res) => {
        setMarketplace(res.data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const fetchMarketplaceLanguageObjects = (objectData: IMarketplaceLanguageObjectsData, lang: string) => {
    setMarketplaceLanguageObjectsIsLoading(true);
    getMarketplaceLanguageObjects(jwt, objectData)
      .then((res) => {
        const { data } = res;
        setMarketplaceLanguageObjects(data);
        const options: Option<string>[] = [];
        objectData.findProperties.forEach((property) => {
          data[property].forEach((propertyValue) => {
            if (propertyValue[lang]) {
              options.push({ label: propertyValue[lang], value: propertyValue[lang] });
            }
          });
        });
        setMarketplaceLanguageObjectsOptions(options);
        setMarketplaceLanguageObjectsIsLoading(false);
      }).catch(() => {
        setMarketplacePropertiesIsLoading(false);
      });
  };

  const requestMarketplaceLanguageObjects = (objectData: IMarketplaceLanguageObjectsData): Promise<{data: IMarketplaceLanguageObjects}> => {
    return new Promise((resolve, reject) => {
      getMarketplaceLanguageObjects(jwt, objectData)
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  };

  const getMarketplace = useCallback((id: string) => {
    return marketplaces?.find((item) => item.id === id);
  }, [marketplaces]);

  useEffect(() => {
    if (!marketplaces?.length) {
      fetchMarketplaces();
    }
  }, [jwt]);

  return {
    marketplaces,
    marketplace,
    getMarketplace,
    marketplaceProperties,
    isLoading,
    marketplacePropertiesIsLoading,
    marketplaceLanguageObjectsIsLoading,
    marketplaceLanguageObjects,
    marketplaceLanguageObjectsOptions,
    fetchMarketplaces,
    fetchShortMarketplace,
    fetchMarketplaceLanguageObjects,
    setMarketplaceLanguageObjectsOptions,
    requestMarketplaceLanguageObjects,
    setMarketplaceProperties,
  };
};
